/**
 * Title timeline — drag-to-scroll viewport + smooth custom scrollbar.
 */
(function () {
  'use strict';

  var DRAG_THRESHOLD = 4;

  function TitleTimeline(root) {
    this.root = root;
    this.viewport = root.querySelector('.title-timeline__viewport');
    this.track = root.querySelector('[data-ttl-track]');
    this.scrollbar = root.querySelector('[data-ttl-scrollbar]');
    this.scrollbarTrack = root.querySelector('[data-ttl-scrollbar-track]');
    this.thumb = root.querySelector('[data-ttl-thumb]');

    this.thumbDragging = false;
    this.thumbDragOffset = 0;
    this.slideDragging = false;
    this.slideMoved = false;
    this.slidePointerId = null;
    this.slideStartX = 0;
    this.slideStartScroll = 0;
    this.slideLastX = 0;
    this.slideLastTime = 0;
    this.slideVelocity = 0;
    this.momentumRaf = null;
    this._raf = null;
    this._updateRaf = null;
    this._snapRestoreTimer = null;

    if (!this.track || !this.scrollbar || !this.scrollbarTrack || !this.thumb) return;
    if (!this.viewport) this.viewport = this.track;

    this._onScroll = this.onScroll.bind(this);
    this._onResize = this.onResize.bind(this);
    this._onThumbPointerDown = this.onThumbPointerDown.bind(this);
    this._onThumbPointerMove = this.onThumbPointerMove.bind(this);
    this._onThumbPointerUp = this.onThumbPointerUp.bind(this);
    this._onScrollbarTrackDown = this.onScrollbarTrackDown.bind(this);
    this._onSlidePointerDown = this.onSlidePointerDown.bind(this);
    this._onSlidePointerMove = this.onSlidePointerMove.bind(this);
    this._onSlidePointerUp = this.onSlidePointerUp.bind(this);
    this._onDragStart = function (e) { e.preventDefault(); };
    this._onClickCapture = this.onClickCapture.bind(this);

    this.bind();
    this.scheduleUpdate();
  }

  TitleTimeline.prototype.bind = function () {
    this.track.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize);

    this.thumb.addEventListener('pointerdown', this._onThumbPointerDown);
    this.scrollbarTrack.addEventListener('pointerdown', this._onScrollbarTrackDown);

    this.viewport.addEventListener('pointerdown', this._onSlidePointerDown);
    this.viewport.addEventListener('dragstart', this._onDragStart);
    this.viewport.addEventListener('click', this._onClickCapture, true);

    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(this._onResize);
      this._ro.observe(this.track);
      this._ro.observe(this.scrollbarTrack);
      Array.prototype.forEach.call(this.track.children, function (child) {
        this._ro.observe(child);
      }, this);
    }

    Array.prototype.forEach.call(this.track.querySelectorAll('img'), function (img) {
      img.setAttribute('draggable', 'false');
      if (img.complete) return;
      img.addEventListener('load', this._onResize, { once: true });
    }, this);
  };

  TitleTimeline.prototype.setSnapEnabled = function (enabled) {
    if (enabled) {
      this.track.style.scrollSnapType = '';
      this.track.classList.remove('is-dragging');
      this.viewport.classList.remove('is-dragging');
    } else {
      this.track.style.scrollSnapType = 'none';
      this.track.classList.add('is-dragging');
      this.viewport.classList.add('is-dragging');
    }
  };

  TitleTimeline.prototype.pauseSnapTemporarily = function () {
    var self = this;
    this.setSnapEnabled(false);
    clearTimeout(this._snapRestoreTimer);
    this._snapRestoreTimer = setTimeout(function () {
      if (!self.thumbDragging && !self.slideDragging) {
        self.setSnapEnabled(true);
      }
    }, 120);
  };

  TitleTimeline.prototype.onScroll = function () {
    if (this._raf) return;
    this._raf = requestAnimationFrame(function () {
      this._raf = null;
      this.updateThumb();
    }.bind(this));
  };

  TitleTimeline.prototype.onResize = function () {
    this.scheduleUpdate();
  };

  TitleTimeline.prototype.scheduleUpdate = function () {
    if (this._updateRaf) return;
    this._updateRaf = requestAnimationFrame(function () {
      this._updateRaf = null;
      this.updateLayout();
    }.bind(this));
  };

  TitleTimeline.prototype.maxScroll = function () {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  };

  TitleTimeline.prototype.contentWidth = function () {
    var items = this.track.children;
    var width = 0;
    for (var i = 0; i < items.length; i++) {
      width += items[i].offsetWidth;
    }
    return width;
  };

  TitleTimeline.prototype.clampScroll = function (value) {
    return Math.min(this.maxScroll(), Math.max(0, value));
  };

  TitleTimeline.prototype.setScrollLeft = function (value, smooth) {
    var next = this.clampScroll(value);
    if (smooth && typeof this.track.scrollTo === 'function') {
      this.track.scrollTo({ left: next, behavior: 'smooth' });
    } else {
      this.track.scrollLeft = next;
    }
  };

  TitleTimeline.prototype.updateLayout = function () {
    var viewW = this.track.clientWidth;
    var contentW = this.contentWidth();
    var fits = contentW > 0 && contentW <= viewW + 1;
    var isDesktop = window.matchMedia('(min-width: 769px)').matches;

    this.root.classList.toggle('is-centered', fits && isDesktop);
    this.track.classList.toggle('is-centered', fits && isDesktop);

    if (fits) {
      this.track.scrollLeft = 0;
    }

    this.updateThumb();
  };

  TitleTimeline.prototype.updateThumb = function () {
    var maxScroll = this.maxScroll();
    var trackWidth = this.scrollbarTrack.clientWidth || this.scrollbarTrack.offsetWidth;

    if (trackWidth <= 0) {
      this.scheduleUpdate();
      return;
    }

    var viewW = this.track.clientWidth;
    var contentW = Math.max(this.contentWidth(), this.track.scrollWidth, 1);
    // Thumb = visible viewport / total content (exact ratio)
    var visibleRatio = Math.min(1, viewW / contentW);
    var thumbWidth = Math.round(trackWidth * visibleRatio);

    // Keep a usable handle only while content actually overflows
    if (maxScroll > 1) {
      thumbWidth = Math.max(40, thumbWidth);
    } else {
      thumbWidth = trackWidth;
    }

    thumbWidth = Math.min(trackWidth, thumbWidth);

    var maxThumbLeft = Math.max(0, trackWidth - thumbWidth);
    var progress = maxScroll > 0 ? this.track.scrollLeft / maxScroll : 0;
    var left = maxThumbLeft * progress;

    this.thumb.style.width = thumbWidth + 'px';
    this.thumb.style.transform = 'translate3d(' + left + 'px, 0, 0)';
  };

  TitleTimeline.prototype.setScrollFromThumbLeft = function (thumbLeft, smooth) {
    var maxScroll = this.maxScroll();
    var trackWidth = this.scrollbarTrack.clientWidth || this.scrollbarTrack.offsetWidth;
    var thumbWidth = this.thumb.offsetWidth || 48;
    var maxThumbLeft = Math.max(0, trackWidth - thumbWidth);
    var clamped = Math.min(maxThumbLeft, Math.max(0, thumbLeft));
    var progress = maxThumbLeft > 0 ? clamped / maxThumbLeft : 0;

    this.thumb.style.transform = 'translate3d(' + clamped + 'px, 0, 0)';
    this.setScrollLeft(progress * maxScroll, !!smooth);
  };

  /* ── Scrollbar thumb drag ── */
  TitleTimeline.prototype.onThumbPointerDown = function (event) {
    if (event.button != null && event.button !== 0) return;
    if (this.root.classList.contains('is-centered') || this.maxScroll() <= 1) return;
    event.preventDefault();
    event.stopPropagation();
    this.stopMomentum();
    this.thumbDragging = true;
    this.setSnapEnabled(false);
    this.root.classList.add('is-scrollbar-dragging');

    var matrix = window.getComputedStyle(this.thumb).transform;
    var currentLeft = 0;
    if (matrix && matrix !== 'none') {
      var parts = matrix.split(',');
      currentLeft = parseFloat(parts[4]) || 0;
    }
    this.thumbDragOffset = event.clientX - this.scrollbarTrack.getBoundingClientRect().left - currentLeft;

    this.thumb.setPointerCapture(event.pointerId);
    this.thumb.addEventListener('pointermove', this._onThumbPointerMove);
    this.thumb.addEventListener('pointerup', this._onThumbPointerUp);
    this.thumb.addEventListener('pointercancel', this._onThumbPointerUp);
  };

  TitleTimeline.prototype.onThumbPointerMove = function (event) {
    if (!this.thumbDragging) return;
    var trackLeft = this.scrollbarTrack.getBoundingClientRect().left;
    this.setScrollFromThumbLeft(event.clientX - trackLeft - this.thumbDragOffset, false);
  };

  TitleTimeline.prototype.onThumbPointerUp = function (event) {
    this.thumbDragging = false;
    this.root.classList.remove('is-scrollbar-dragging');
    try {
      this.thumb.releasePointerCapture(event.pointerId);
    } catch (e) {}
    this.thumb.removeEventListener('pointermove', this._onThumbPointerMove);
    this.thumb.removeEventListener('pointerup', this._onThumbPointerUp);
    this.thumb.removeEventListener('pointercancel', this._onThumbPointerUp);
    this.pauseSnapTemporarily();
    this.updateThumb();
  };

  TitleTimeline.prototype.onScrollbarTrackDown = function (event) {
    if (event.target === this.thumb || this.thumb.contains(event.target)) return;
    if (event.button != null && event.button !== 0) return;
    if (this.root.classList.contains('is-centered') || this.maxScroll() <= 1) return;
    event.preventDefault();
    this.stopMomentum();
    this.pauseSnapTemporarily();
    var trackLeft = this.scrollbarTrack.getBoundingClientRect().left;
    var thumbWidth = this.thumb.offsetWidth || 48;
    this.setScrollFromThumbLeft(event.clientX - trackLeft - thumbWidth / 2, true);
  };

  /* ── Viewport / track slide drag ── */
  TitleTimeline.prototype.onSlidePointerDown = function (event) {
    if (event.button != null && event.button !== 0) return;
    if (event.target.closest && event.target.closest('[data-ttl-thumb], [data-ttl-scrollbar]')) return;
    if (this.root.classList.contains('is-centered')) return;
    if (this.maxScroll() <= 0) return;

    this.stopMomentum();
    this.slideDragging = true;
    this.slideMoved = false;
    this.slidePointerId = event.pointerId;
    this.slideStartX = event.clientX;
    this.slideLastX = event.clientX;
    this.slideLastTime = performance.now();
    this.slideVelocity = 0;
    this.slideStartScroll = this.track.scrollLeft;

    this.viewport.setPointerCapture(event.pointerId);
    this.viewport.addEventListener('pointermove', this._onSlidePointerMove);
    this.viewport.addEventListener('pointerup', this._onSlidePointerUp);
    this.viewport.addEventListener('pointercancel', this._onSlidePointerUp);
  };

  TitleTimeline.prototype.onSlidePointerMove = function (event) {
    if (!this.slideDragging || event.pointerId !== this.slidePointerId) return;

    var dx = event.clientX - this.slideStartX;
    if (!this.slideMoved && Math.abs(dx) < DRAG_THRESHOLD) return;

    if (!this.slideMoved) {
      this.slideMoved = true;
      this.setSnapEnabled(false);
      this.root.classList.add('is-slide-dragging');
    }

    event.preventDefault();

    var now = performance.now();
    var dt = Math.max(1, now - this.slideLastTime);
    var frameDx = event.clientX - this.slideLastX;
    this.slideVelocity = frameDx / dt;
    this.slideLastX = event.clientX;
    this.slideLastTime = now;

    this.setScrollLeft(this.slideStartScroll - dx, false);
    this.updateThumb();
  };

  TitleTimeline.prototype.onSlidePointerUp = function (event) {
    if (event.pointerId !== this.slidePointerId) return;

    var moved = this.slideMoved;
    var velocity = this.slideVelocity;

    this.slideDragging = false;
    this.slidePointerId = null;
    this.root.classList.remove('is-slide-dragging');

    try {
      this.viewport.releasePointerCapture(event.pointerId);
    } catch (e) {}
    this.viewport.removeEventListener('pointermove', this._onSlidePointerMove);
    this.viewport.removeEventListener('pointerup', this._onSlidePointerUp);
    this.viewport.removeEventListener('pointercancel', this._onSlidePointerUp);

    if (moved) {
      this.startMomentum(velocity);
    } else {
      this.setSnapEnabled(true);
    }
  };

  TitleTimeline.prototype.onClickCapture = function (event) {
    if (!this.slideMoved) return;
    event.preventDefault();
    event.stopPropagation();
    this.slideMoved = false;
  };

  TitleTimeline.prototype.stopMomentum = function () {
    if (this.momentumRaf) {
      cancelAnimationFrame(this.momentumRaf);
      this.momentumRaf = null;
    }
  };

  TitleTimeline.prototype.startMomentum = function (velocityPxPerMs) {
    var self = this;
    var velocity = velocityPxPerMs * 16; // approx px per frame
    var friction = 0.95;
    var minVelocity = 0.35;

    this.stopMomentum();
    this.setSnapEnabled(false);

    function step() {
      if (Math.abs(velocity) < minVelocity) {
        self.momentumRaf = null;
        self.pauseSnapTemporarily();
        self.updateThumb();
        return;
      }

      self.setScrollLeft(self.track.scrollLeft - velocity, false);
      self.updateThumb();
      velocity *= friction;

      // Bounce soft-stop at edges
      if (self.track.scrollLeft <= 0 || self.track.scrollLeft >= self.maxScroll()) {
        velocity = 0;
      }

      self.momentumRaf = requestAnimationFrame(step);
    }

    this.momentumRaf = requestAnimationFrame(step);
  };

  function init(root) {
    if (!root || root._ttlInit) return;
    root._ttlInit = true;
    new TitleTimeline(root);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-ttl-root]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event || !event.target) return;
    var roots = event.target.querySelectorAll('[data-ttl-root]');
    Array.prototype.forEach.call(roots, function (root) {
      root._ttlInit = false;
      init(root);
    });
    if (event.target.matches && event.target.matches('[data-ttl-root]')) {
      event.target._ttlInit = false;
      init(event.target);
    }
  });
})();
